/**
 * What: Owns the semantic provenance contract for the Shopify RFM customer-group frame.
 * Transport / data-flow: Pure structural validation shared by Web Audience Set persistence and Shopify plan/packet boundaries.
 *
 * Algorithm or layout: Validate the frame's provider, signal, scope, required
 * framing metadata, and optional source buckets without importing either app.
 */
import { isRecord } from '@testfeed/core';
export const SHOPIFY_RFM_FRAME_SOURCE_SURFACE = 'shopify_admin';
export const SHOPIFY_RFM_FRAME_FINGERPRINT_PREFIX = 'shopify-rfm-frame_';
function nonEmptyString(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
/** Validates the opaque current-source identity stored with a reusable Shopify frame. */
export function validateShopifyRfmGroupFrameFingerprint(value) {
    if (typeof value !== 'string' ||
        !value.startsWith(SHOPIFY_RFM_FRAME_FINGERPRINT_PREFIX) ||
        value.length <= SHOPIFY_RFM_FRAME_FINGERPRINT_PREFIX.length) {
        return ['Shopify customer-group frame requires a current-source fingerprint'];
    }
    return [];
}
/** Throws the first actionable fingerprint error for an explicit Shopify frame. */
export function assertShopifyRfmGroupFrameFingerprint(value) {
    const [error] = validateShopifyRfmGroupFrameFingerprint(value);
    if (error)
        throw new Error(error);
}
/** Returns actionable semantic errors for an explicit Shopify customer-group frame. */
export function validateShopifyRfmGroupFrameProvenance(value) {
    const errors = [];
    if (!isRecord(value)) {
        return ['Shopify customer-group frame provenance must be an object'];
    }
    if (value.provider !== 'shopify') {
        errors.push('Shopify customer-group frame provenance must use provider "shopify"');
    }
    if (value.signalKind !== 'purchase_history') {
        errors.push('Shopify customer-group frame provenance must use signal "purchase_history"');
    }
    if (value.sourceScopeType !== 'shopify') {
        errors.push('Shopify customer-group frame provenance must use source scope "shopify"');
    }
    if (!nonEmptyString(value.frameScope)) {
        errors.push('Shopify customer-group frame provenance requires a non-empty frame scope');
    }
    if (!nonEmptyString(value.derivationMethod)) {
        errors.push('Shopify customer-group frame provenance requires a non-empty derivation method');
    }
    if (value.sourceBuckets === undefined)
        return errors;
    if (!Array.isArray(value.sourceBuckets)) {
        errors.push('Shopify customer-group frame provenance source buckets must be an array');
        return errors;
    }
    value.sourceBuckets.forEach((bucket, index) => {
        const path = `Shopify customer-group frame provenance source bucket ${index}`;
        if (!isRecord(bucket)) {
            errors.push(`${path} must be an object`);
            return;
        }
        if (bucket.provider !== 'shopify') {
            errors.push(`${path} must use provider "shopify"`);
        }
        if (bucket.sourceSurface !== SHOPIFY_RFM_FRAME_SOURCE_SURFACE) {
            errors.push(`${path} must use source surface "${SHOPIFY_RFM_FRAME_SOURCE_SURFACE}" for provider "shopify"`);
        }
        if (!nonEmptyString(bucket.bucketStart) || !nonEmptyString(bucket.bucketEnd)) {
            errors.push(`${path} requires non-empty bucketStart and bucketEnd`);
        }
        if (!Array.isArray(bucket.recordIds) ||
            bucket.recordIds.length === 0 ||
            bucket.recordIds.some((recordId) => !nonEmptyString(recordId))) {
            errors.push(`${path} requires a non-empty recordIds array of strings`);
        }
    });
    return errors;
}
/** Throws the first actionable semantic error for an explicit Shopify frame. */
export function assertShopifyRfmGroupFrameProvenance(value) {
    const [error] = validateShopifyRfmGroupFrameProvenance(value);
    if (error)
        throw new Error(error);
}
//# sourceMappingURL=shopify-rfm-frame-provenance.js.map